import { ThemePreference } from "@kalyzee/kalyzee-ui";
import { LayoutType } from "../interfaces/ui";

export enum StorageCategory {
  TOKEN = 'token',
  SETTINGS = 'settings',
  EXPERT = 'expert',
  MEDIA = "media",
  UI = "ui",
  GUEST = "guest",
  IMPERSONATION = "impersonation",
}

export enum TokenType {
  Normal = 'normal',
  Refresh = 'refreshToken',
}

export enum MediaKeys {
  INTPUT_VIDEO_ENABLED = 'intput_video_enabled',
  INTPUT_AUDIO_ENABLED = 'intput_audio_enabled',
  SOURCE_PREFERENCES = 'source_preferences',
}

export enum UiKeys {
  LAYOUT = 'layout',
  THEME = 'theme',
}

export enum ImpersonationKeys {
  TARGET = 'target',
  // The actor's own permissions are only readable while NOT impersonating:
  // once the header is set, `getMe()` answers for the target. Remembering that
  // the actor was allowed is what keeps the bar reachable to leave ghost mode.
  ALLOWED = 'allowed',
  COLLAPSED = 'collapsed',
}

export interface StorageKeysMap {
  [StorageCategory.TOKEN]: TokenType,
  [StorageCategory.SETTINGS]: undefined,
  [StorageCategory.EXPERT]: 'home_params',
  [StorageCategory.MEDIA]: MediaKeys,
  [StorageCategory.UI]: UiKeys,
  [StorageCategory.GUEST]: 'credentials_by_meeting',
  [StorageCategory.IMPERSONATION]: ImpersonationKeys,
}

const storage = localStorage;

// ---------------- NATIVE ------------------ //

const generateKey = (category : StorageCategory, key?: string) => (
  key ? `${category}_${key}` : category
);

const setItem = <T extends StorageCategory>(
  category : T,
  key: StorageKeysMap[T],
  value: string,
) => storage.setItem(generateKey(category, key), value);

const getItem = <T extends StorageCategory>(
  category : T,
  key?: StorageKeysMap[T],
) => storage.getItem(generateKey(category, key));

// ---------------- TOKEN ------------------ //

export const setToken = (
  token?: string,
  tokenType: TokenType = TokenType.Normal,
) => {
  if (token) setItem(StorageCategory.TOKEN, tokenType, token);
  else storage.removeItem(generateKey(StorageCategory.TOKEN, tokenType));
};

export const getToken = (
  tokenType: TokenType = TokenType.Normal,
) => getItem(StorageCategory.TOKEN, tokenType);

// ---------------- SETTINGS ------------------ //

export const setSettings = (
  settings: string
) => setItem(StorageCategory.SETTINGS, undefined, settings);

export const getSettings = () => getItem(StorageCategory.SETTINGS);

// ---------------- SETTINGS (multi-config) ------------------ //
// Each config lives under `settings-<slug>`; `selected-settings-key` points to
// the active one. The plain `settings` key above is the legacy single-config
// storage, still read as fallback for the default config.

export const SETTINGS_KEY_PREFIX = 'settings-';
export const SETTINGS_DEFAULT_KEY = `${SETTINGS_KEY_PREFIX}default`;
export const SETTINGS_LEGACY_KEY: string = StorageCategory.SETTINGS;
export const SELECTED_SETTINGS_STORAGE_KEY = 'selected-settings-key';

export const getSelectedSettingsKey = () => storage.getItem(SELECTED_SETTINGS_STORAGE_KEY);

export const setSelectedSettingsKey = (
  key: string
) => storage.setItem(SELECTED_SETTINGS_STORAGE_KEY, key);

export const getSettingsByKey = (key: string) => storage.getItem(key);

export const setSettingsByKey = (
  key: string,
  value: string
) => storage.setItem(key, value);

export const removeSettingsByKey = (key: string) => storage.removeItem(key);

export const listSettingsKeys = (): string[] =>
  Object.keys(storage).filter((k) => k.startsWith(SETTINGS_KEY_PREFIX));

// ---------------- SETTINGS (sources) ------------------ //
// Per-config side data: where the config takes its content from (absent or
// `local` = the config key holds the settings, edited in the app; `remote` =
// the config key only caches what was last fetched from `url`), plus the
// variable values substituted into its `[[NAME]]` placeholders.
// The key deliberately uses an underscore: `settings-` prefixed keys are
// enumerated as configs by `listSettingsKeys`.

export const SETTINGS_SOURCES_STORAGE_KEY = 'settings_sources';

export interface StorageSettingsVariable {
  name: string;
  value: string;
}

export interface StorageSettingsSource {
  /** `remote` requires `url`; a `local` entry only carries variables. */
  mode: 'local' | 'remote';
  url?: string;
  /** Timestamp (ms) of the last successful fetch — drives the popup status line. */
  fetchedAt?: number;
  /**
   * Values substituted into the `[[NAME]]` placeholders of the config. Local by
   * default — that is what lets one shared remote template serve several rooms
   * (and keeps secrets off the server). When `variablesRemote` is set they are
   * fetched from that URL instead, and this list only caches the last fetch.
   */
  variables?: StorageSettingsVariable[];
  /** Set when the values themselves come from a URL rather than this machine. */
  variablesRemote?: {
    url: string;
    /** Timestamp (ms) of the last successful fetch. */
    fetchedAt?: number;
  };
}

export type StorageSettingsSources = { [configKey: string]: StorageSettingsSource };

export const getSettingsSources = (): StorageSettingsSources => {
  const str = storage.getItem(SETTINGS_SOURCES_STORAGE_KEY);
  if (!str) return {};
  try {
    const parsed = JSON.parse(str);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
  } catch (err) {}
  return {};
};

export const setSettingsSources = (sources: StorageSettingsSources) => {
  storage.setItem(SETTINGS_SOURCES_STORAGE_KEY, JSON.stringify(sources));
};

// ---------------- EXPERT ------------------ //

export interface StorageExpertHomeParams {
  micNotSynced?: boolean;
}

export const setExpertHomeParams = (
  params: StorageExpertHomeParams
) => setItem(StorageCategory.EXPERT, "home_params", JSON.stringify(params));

export const getExpertHomeParams = (): StorageExpertHomeParams | undefined => {
  const str = getItem(StorageCategory.EXPERT, "home_params");
  if (!str) return undefined;
  try {
    return JSON.parse(str);
  } catch(err) {}
  return undefined;
};

// ---------------- GUEST ------------------ //

// Guest credentials live in their own storage key (independent from TOKEN) so they
// survive logout and can be reused to resume the same guest identity later on.
// They are bound to the meeting they were created for: one entry per meeting id.
// Reading/writing them is gated by the `features.rememberGuests` flag at call
// sites — these helpers stay flag-agnostic.
export interface StorageGuestCredentials {
  id: string;
  username?: string;
  token: string;
  refreshToken: string;
}

type StorageGuestCredentialsByMeeting = { [meetingId: string]: StorageGuestCredentials };

const getGuestCredentialsMap = (): StorageGuestCredentialsByMeeting => {
  const str = getItem(StorageCategory.GUEST, "credentials_by_meeting");
  if (!str) return {};
  try {
    const parsed = JSON.parse(str);
    if (parsed && typeof parsed === "object") return parsed;
  } catch(err) {}
  return {};
};

export const setGuestCredentialsForMeeting = (
  meetingId: string,
  credentials: StorageGuestCredentials,
) => {
  const map = getGuestCredentialsMap();
  map[meetingId] = credentials;
  setItem(StorageCategory.GUEST, "credentials_by_meeting", JSON.stringify(map));
};

export const getGuestCredentialsForMeeting = (
  meetingId: string
): StorageGuestCredentials | undefined => getGuestCredentialsMap()[meetingId];

export const clearGuestCredentials = () => storage.removeItem(
  generateKey(StorageCategory.GUEST, "credentials_by_meeting")
);

// ---------------- IMPERSONATION ------------------ //

export interface StorageImpersonationTarget {
  /** User id, or an email — which designates an identity, and through it a user. */
  target: string;
  /** Narrows a user id to one of its identities. Unused on an email. */
  identityId?: string;
}

export const setImpersonationTarget = (
  target?: StorageImpersonationTarget,
) => {
  const key = generateKey(StorageCategory.IMPERSONATION, ImpersonationKeys.TARGET);
  if (target) storage.setItem(key, JSON.stringify(target));
  else storage.removeItem(key);
};

export const getImpersonationTarget = (): StorageImpersonationTarget | undefined => {
  const str = getItem(StorageCategory.IMPERSONATION, ImpersonationKeys.TARGET);
  if (!str) return undefined;
  try {
    const parsed = JSON.parse(str);
    if (parsed && typeof parsed.target === "string" && parsed.target) return parsed;
  } catch(err) {}
  return undefined;
};

export const setImpersonationAllowed = (allowed: boolean) => {
  const key = generateKey(StorageCategory.IMPERSONATION, ImpersonationKeys.ALLOWED);
  if (allowed) storage.setItem(key, "true");
  else storage.removeItem(key);
};

export const getImpersonationAllowed = (): boolean => (
  getItem(StorageCategory.IMPERSONATION, ImpersonationKeys.ALLOWED) === "true"
);

// Persisted rather than component state: every target change reloads the app,
// so a collapsed bar would pop back open on each switch.
export const setImpersonationCollapsed = (collapsed: boolean) => setItem(
  StorageCategory.IMPERSONATION,
  ImpersonationKeys.COLLAPSED,
  collapsed ? "true" : "false",
);

/** Collapsed by default: the bar's message is enough, the picker is on demand. */
export const getImpersonationCollapsed = (): boolean => (
  getItem(StorageCategory.IMPERSONATION, ImpersonationKeys.COLLAPSED) !== "false"
);

// ---------------- Media ------------------ //

export interface SourcePreference {
  audioEnabled?: boolean;
  videoEnabled?: boolean;
}

export interface MediaData {
  [MediaKeys.INTPUT_VIDEO_ENABLED]: boolean;
  [MediaKeys.INTPUT_AUDIO_ENABLED]: boolean;
  [MediaKeys.SOURCE_PREFERENCES]: { [sourceName: string]: SourcePreference };
}

export const setMediaParam = <T extends MediaKeys>(
  key: T,
  data: MediaData[T]
) => setItem(StorageCategory.MEDIA, key, JSON.stringify(data));

export const getMediaParam = <T extends MediaKeys>(key: T): MediaData[T] | null => {
  const str = getItem(StorageCategory.MEDIA, key);
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch(err) {}
  return null;
};

// ---------------- UI ------------------ //

export interface UiData {
  [UiKeys.LAYOUT]: LayoutType;
  [UiKeys.THEME]: ThemePreference;
}

export const setUiParam = <T extends UiKeys>(
  key: T,
  data: UiData[T]
) => setItem(StorageCategory.UI, key, JSON.stringify(data));

export const getUiParam = <T extends UiKeys>(key: T): UiData[T] | null => {
  const str = getItem(StorageCategory.UI, key);
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch(err) {}
  return null;
};

// ---------------- Theme (UI shortcuts) ------------------ //

const setTheme = (theme: ThemePreference) => setUiParam(UiKeys.THEME, theme);

const getTheme = (): ThemePreference | null => getUiParam(UiKeys.THEME);

const Storage = {
  // TOKEN
  setToken,
  getToken,
  // SETTINGS
  setSettings,
  getSettings,
  // EXPERT
  setExpertHomeParams,
  getExpertHomeParams,
  // GUEST
  setGuestCredentialsForMeeting,
  getGuestCredentialsForMeeting,
  clearGuestCredentials,
  // MEDIA
  setMediaParam,
  getMediaParam,
  // UI
  setUiParam,
  getUiParam,
  // THEME
  setTheme,
  getTheme,
};

export default Storage;
